import { observer } from 'mobx-react';
import { FC, Fragment, useMemo, useState } from 'react';

import { Button, Combobox, Flex, Text } from '@servicetitan/anvil2';
import { useDependencies } from '@servicetitan/react-ioc';

import { BottomDrawerSelect } from '../../common/components/bottom-drawer-select/bottom-drawer-select';
import { OemPart } from '../models/oem-part';
import { ReplacementPartFinderStore } from '../stores/replacement-part-finder.store';

interface OemPartSelectProps {
    onSubmit: (equipment: OemPart) => void;
}

export const OemPartSelect: FC<OemPartSelectProps> = observer(({ onSubmit }) => {
    const [store] = useDependencies<[ReplacementPartFinderStore]>(ReplacementPartFinderStore);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [selectedOemPart, setSelectedOemPart] = useState<OemPart | undefined>(
        store.selectedOemPart
    );

    const options = useMemo(() => {
        const query = searchText.toLowerCase();
        return store.oemParts.filter(e => e.label?.toLowerCase().includes(query));
    }, [store.oemParts, searchText]);

    const handleSubmit = () => {
        if (selectedOemPart) {
            onSubmit(selectedOemPart);
        }
    };

    return (
        <Fragment>
            <Flex direction="column" gap="3">
                <Text size="small">What OEM part do you want to find a replacement for?</Text>
                <div onClick={() => setDrawerOpen(true)}>
                    <Combobox
                        open={false}
                        items={[selectedOemPart]}
                        selectedItem={selectedOemPart}
                        onChange={(item: OemPart) => {
                            setSelectedOemPart(item);
                            setDrawerOpen(false);
                        }}
                        itemToString={item => (item ? `${item?.label} • ${item?.partNumber}` : '')}
                        itemToKey={item => (item ? item.id : null)}
                    >
                        <Combobox.SelectTrigger
                            value={
                                selectedOemPart
                                    ? `${selectedOemPart?.label ?? ''} • ${selectedOemPart?.partNumber}`
                                    : ''
                            }
                            label="OEM Part"
                            placeholder="Select OEM part"
                        />
                    </Combobox>
                </div>
                <Flex>
                    <Button
                        appearance="primary"
                        disabled={!selectedOemPart}
                        loading={store.isLoading}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Flex>
            </Flex>
            <BottomDrawerSelect
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                dropdownOptions={options}
                itemToKey={item => item.id}
                searchPlaceholder="Search OEM part"
                onSearchQueryChange={setSearchText}
                onChange={item => {
                    setSelectedOemPart(item);
                    setDrawerOpen(false);
                }}
                value={selectedOemPart}
                itemRenderer={item => {
                    return (
                        <Flex direction="column">
                            <Text>
                                {item.label} • {item.partNumber}
                            </Text>
                            {item.disabledReason && <Text>{item.disabledReason}</Text>}
                        </Flex>
                    );
                }}
            />
        </Fragment>
    );
});
